import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  ActionCodeSettings,
  applyActionCode,
  Auth,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
  UserCredential,
  verifyPasswordResetCode,
} from '@angular/fire/auth';
import { User } from '@firebase/auth';
import { rxState } from '@rx-angular/state';
import { selectSlice } from '@rx-angular/state/selections';
import { map, Observable } from 'rxjs';
import { AuthProvider } from '../enums/auth-provider.enum';
import { RoutePath } from '../enums/routes.enum';
import { LocaleService } from '../i18n/locale.service';

export type AuthGuardData = {
  token: string | null;
  emailVerified: boolean;
};

type AuthState = {
  user: User | null;
  emailVerified: boolean;
  token: string | null;
  name: string | null;
  email: string | null;
  picture: string | null;
  hasPasswordProvider: boolean;
};

@Injectable({ providedIn: 'root' })
export class FirebaseAuthService implements OnDestroy {
  private readonly auth = inject(Auth);
  private readonly i18n = inject(LocaleService);

  readonly state = rxState<AuthState>();

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private readonly refreshTokenTime = 50 * 60 * 1000; // 50 minutes;
  private tokenRefreshInterval?: ReturnType<typeof setInterval>;

  isLoggedIn$ = this.state.select('token').pipe(map((token) => token != null));

  authGuardData$: Observable<AuthGuardData> = this.state.select([
    'token',
    'emailVerified',
  ]);

  authUsername$ = this.state
    .select(selectSlice(['name', 'email']))
    .pipe(map(({ name, email }) => name || email || 'User'));

  authEmail$ = this.state.select('email');

  profilePicture$ = this.state.select('picture');

  hasPasswordProvider$ = this.state.select('hasPasswordProvider');

  googleAuthProvider = new GoogleAuthProvider();

  private readonly REDIRECT_URL_KEY = 'essentee_redirect_url';
  readonly redirectUrl = signal<string | null>(
    localStorage.getItem(this.REDIRECT_URL_KEY),
  );

  async getToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (user == null) {
      return null;
    }
    return await user.getIdToken(forceRefresh);
  }

  constructor() {
    this.auth.languageCode = this.i18n.browserLang;

    this.auth.onIdTokenChanged(async (currentUser) => {
      this.auth.languageCode = this.i18n.browserLang;

      if (currentUser == null) {
        this.stopTokenRefresh();
        this.initState();
        return;
      }

      this.startTokenRefresh();
      const token = await currentUser.getIdToken();
      this.state.set({
        user: currentUser,
        emailVerified: currentUser.emailVerified,
        token,
        name: currentUser.displayName,
        email: currentUser.email,
        picture: currentUser.photoURL || '/images/user-avatar.png',
        hasPasswordProvider: currentUser.providerData.some(
          (p) => p.providerId === AuthProvider.Password,
        ),
      });
    });
  }

  private startTokenRefresh(): void {
    if (this.tokenRefreshInterval) {
      return;
    }
    this.tokenRefreshInterval = setInterval(() => {
      this.getToken(true);
    }, this.refreshTokenTime);
  }

  private stopTokenRefresh(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopTokenRefresh();
  }

  private initState(): void {
    this.state.set({
      token: null,
      name: null,
      email: null,
      picture: null,
      emailVerified: false,
      user: null,
      hasPasswordProvider: false,
    });
  }

  setRedirectUrl(url: string | null): void {
    if (url) {
      localStorage.setItem(this.REDIRECT_URL_KEY, url);
    } else {
      localStorage.removeItem(this.REDIRECT_URL_KEY);
    }
    this.redirectUrl.set(url);
  }

  login(email: string, password: string): Promise<UserCredential> {
    this.auth.languageCode = this.i18n.browserLang;
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  register(email: string, password: string): Promise<UserCredential> {
    this.auth.languageCode = this.i18n.browserLang;
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  sendEmailVerification(redirectRoute?: string): Promise<void> {
    this.auth.languageCode = this.i18n.browserLang;
    // Use auth.currentUser (synchronously set by Firebase after sign-in/register)
    // instead of rxState user (updated asynchronously via onIdTokenChanged → getIdToken())
    const user = this.auth.currentUser;
    if (user == null) {
      return Promise.reject(new Error('User not authenticated'));
    }
    if (user.email == null) {
      return Promise.reject(new Error('User email not set'));
    }

    const verifyUrl = new URL(
      `/${RoutePath.AuthAction}`,
      window.location.origin,
    );
    if (redirectRoute) {
      verifyUrl.searchParams.set('continue', redirectRoute);
    }

    return sendEmailVerification(user, {
      url: verifyUrl.toString(),
    });
  }

  googleSignIn(): Promise<UserCredential> {
    this.auth.languageCode = this.i18n.browserLang;
    return signInWithPopup(this.auth, this.googleAuthProvider);
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    this.auth.languageCode = this.i18n.browserLang;
    const resetUrl = new URL(
      `/${RoutePath.AuthAction}`,
      window.location.origin,
    );
    resetUrl.searchParams.set('email', email);

    const actionCodeSettings: ActionCodeSettings = {
      url: resetUrl.toString(),
    };
    return sendPasswordResetEmail(this.auth, email, actionCodeSettings);
  }

  async verifyEmail(actionCode: string): Promise<void> {
    await applyActionCode(this.auth, actionCode);
    const user = this.auth.currentUser;
    if (user) {
      await user.reload();
      this.state.set({ emailVerified: user.emailVerified });
    }
  }

  verifyPasswordResetCode(actionCode: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, actionCode);
  }

  confirmPasswordReset(actionCode: string, newPassword: string): Promise<void> {
    return confirmPasswordReset(this.auth, actionCode, newPassword);
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const user = this.auth.currentUser;
    if (user == null || user.email == null) {
      throw new Error('User not authenticated');
    }

    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword,
    );
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }
}
